import React, { useState, useEffect } from 'react';
import {
  getAllVectorData,
  deleteVectorData,
  syncUpworkProfile,
  getSyncStatus,
} from '../services/vectorDataService';
import {
  Plus, Trash2, Search, Eye, Edit, FileText,
  Loader, Cloud, User, Briefcase, Award, Code, FolderOpen, FileCode, CheckCircle2, AlertCircle,
} from 'lucide-react';
import AIKnowledgeForm from './AIKnowledgeForm';
import AIKnowledgeViewModal from './AIKnowledgeViewModal';
import { usePermissions } from '../hooks/usePermissions';
import { AdminPageLayout } from './AdminPageLayout';
import { AdminCard } from './AdminCard';

const TYPE_GROUPS = [
  { id: '', label: 'All Types', icon: FileText, types: '' },
  { id: 'about', label: 'Profile', icon: User, types: 'about' },
  { id: 'work_samples', label: 'Work Samples', icon: FolderOpen, types: 'past_projects,services' },
  { id: 'testimonials', label: 'Testimonials', icon: Award, types: 'testimonials' },
  { id: 'skills', label: 'Skills', icon: Code, types: 'skills' },
  { id: 'proposals', label: 'Proposals', icon: FileCode, types: 'proposal' },
];

// Map raw type to friendly label
const TYPE_LABELS = {
  about: 'Profile',
  services: 'Services',
  testimonials: 'Testimonials',
  skills: 'Skills',
  past_projects: 'Past Projects',
  education: 'Education',
  proposal: 'Proposals',
};

// Get the display name for a row based on its type
function getItemName(item) {
  const type = item.type || item.metadata?.type;
  if (type === 'proposal') return item.metadata?.jobTitle || 'Untitled';
  if (type === 'skills') return item.metadata?.skill_name || 'Untitled';
  if (type === 'past_projects') return item.metadata?.project_name || 'Untitled';
  return item.metadata?.name || item.text?.substring(0, 60) || 'Untitled';
}

export function AIKnowledge() {
  const { canCreateVectorData, canUpdateVectorData, canDeleteVectorData } = usePermissions();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [typeCounts, setTypeCounts] = useState({});
  const [limit] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [viewingItem, setViewingItem] = useState(null);
  const [viewingIndex, setViewingIndex] = useState(-1);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncFeedback, setSyncFeedback] = useState(null);

  const currentGroup = TYPE_GROUPS.find(g => g.id === activeFilter) || TYPE_GROUPS[0];

  useEffect(() => {
    fetchData();
  }, [activeFilter, page, searchTerm]);

  useEffect(() => {
    fetchSyncStatus();
  }, []);

  const fetchSyncStatus = async () => {
    try {
      const response = await getSyncStatus();
      if (response.success && response.lastSync) {
        setLastSyncTime(new Date(response.lastSync));
      }
    } catch {
      // Non-critical
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAllVectorData({
        page,
        limit,
        search: searchTerm,
        type: currentGroup.types,
      });
      setData(response.data || []);
      setTotalPages(response.pages || 1);
      setTotal(response.total || 0);
      if (response.typeCounts) setTypeCounts(response.typeCounts);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingItem(null);
    setIsFormOpen(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setIsFormOpen(true);
  };

  const handleView = (item, index) => {
    setViewingItem(item);
    setViewingIndex(index);
    setIsViewOpen(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      const type = item.type || item.metadata?.type;
      await deleteVectorData(type, item.vectorId);
      setSyncFeedback({
        tone: 'success',
        title: 'Entry deleted',
        message: 'The AI knowledge entry was removed successfully.',
        details: [],
      });
      fetchData();
    } catch (err) {
      setSyncFeedback({
        tone: 'error',
        title: 'Delete failed',
        message: err.message || 'The AI knowledge entry could not be deleted.',
        details: [],
      });
    }
  };

  const handleSyncFromUpwork = async () => {
    if (isSyncing) return;
    setIsSyncing(true);
    setError(null);
    setSyncFeedback({
      tone: 'info',
      title: 'Sync in progress',
      message: 'Pulling your Upwork profile into Propel AI knowledge.',
      details: [],
    });
    try {
      const result = await syncUpworkProfile();
      if (result.synced) {
        setSyncFeedback({
          tone: result.counts?.errors ? 'warning' : 'success',
          title: result.counts?.errors ? 'Profile synced with a few issues' : 'AI knowledge synced',
          message: result.message,
          details: result.sampleErrors || [],
        });
        fetchData();
        fetchSyncStatus();
      } else {
        let msg = result.message || 'Failed to sync';
        if (result.reason === 'no_credentials') msg = 'Upwork not connected. Go to Settings > Integrations to connect.';
        else if (result.reason === 'auth_failed') msg = 'Upwork connection expired. Please reconnect.';
        else if (result.reason === 'no_profile') msg = 'No freelancer profile found on Upwork.';
        setSyncFeedback({
          tone: 'error',
          title: 'Profile sync failed',
          message: msg,
          details: result.sampleErrors || [],
        });
      }
    } catch (err) {
      setSyncFeedback({
        tone: 'error',
        title: 'Profile sync failed',
        message: err.message || 'Failed to sync from Upwork',
        details: [],
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const formatLastSync = (date) => {
    if (!date) return null;
    const diff = Math.floor((Date.now() - date) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const totalItems = Object.values(typeCounts).reduce((sum, c) => sum + c, 0);
  const feedbackToneClasses = syncFeedback?.tone === 'success'
    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
    : syncFeedback?.tone === 'warning'
      ? 'border-amber-200 bg-amber-50 text-amber-700'
      : syncFeedback?.tone === 'error'
        ? 'border-rose-200 bg-rose-50 text-rose-700'
        : 'border-primary-200 bg-primary-50 text-primary-700';
  const feedbackIcon = syncFeedback?.tone === 'success'
    ? <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
    : syncFeedback?.tone === 'warning' || syncFeedback?.tone === 'error'
      ? <AlertCircle className="w-4 h-4 flex-shrink-0" />
      : <Loader className="w-4 h-4 flex-shrink-0 animate-spin" />;

  return (
    <AdminPageLayout>
      <AdminCard bodyClassName="space-y-5">
        {/* Stats Bar */}
        <div className="flex items-center gap-4 text-xs text-gray-500">
        <span className="font-medium text-gray-700">{totalItems} items</span>
        {Object.entries(typeCounts).map(([type, count]) => (
          <span key={type}>{TYPE_LABELS[type] || type}: {count}</span>
        ))}
        {lastSyncTime && (
          <span className="ml-auto">Last sync: {formatLastSync(lastSyncTime)}</span>
        )}
        </div>

        {syncFeedback && (
          <div className={`rounded-lg border px-4 py-3 ${feedbackToneClasses}`}>
            <div className="flex items-start gap-3">
              {feedbackIcon}
              <div className="min-w-0">
                <p className="text-sm font-semibold">{syncFeedback.title}</p>
                <p className="mt-1 text-sm">{syncFeedback.message}</p>
                {syncFeedback.details?.length > 0 && (
                  <div className="mt-2 space-y-1 text-xs opacity-90">
                    {syncFeedback.details.slice(0, 3).map((detail) => (
                      <p key={detail}>{detail}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Filter + Search + Actions */}
        <div className="admin-filter-panel flex flex-wrap items-center gap-3">
          <select
            value={activeFilter}
            onChange={(e) => { setActiveFilter(e.target.value); setPage(1); }}
            className="admin-filter-control w-auto min-w-[180px]"
          >
            {TYPE_GROUPS.map(g => (
              <option key={g.id} value={g.id}>{g.label}</option>
            ))}
          </select>

          <div className="flex-1 min-w-[220px] max-w-md relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              placeholder="Search..."
              className="admin-filter-control pl-8 pr-3 bg-white placeholder:text-gray-400"
            />
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={handleSyncFromUpwork}
              disabled={isSyncing || !canCreateVectorData()}
              className="flex items-center gap-1.5 px-2.5 py-1 text-sm font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-md hover:bg-primary-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSyncing ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Cloud className="w-3.5 h-3.5" />}
              {isSyncing ? 'Syncing...' : 'Sync from Upwork'}
            </button>
            {canCreateVectorData() && (
              <button
                onClick={handleCreate}
                data-onboarding="add-ai-knowledge-button"
                className="flex items-center gap-1.5 px-2.5 py-1 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                Add manually
              </button>
            )}
          </div>
        </div>

        {/* List */}
        {loading && !data.length ? (
          <div className="text-center py-16">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm text-gray-500">Loading...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        ) : data.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <FileText className="w-5 h-5 text-gray-400" />
            </div>
            <h3 className="text-sm font-medium text-gray-900 mb-1">No data found</h3>
            <p className="text-sm text-gray-500 mb-4">Add AI knowledge to improve proposal generation.</p>
            {canCreateVectorData() && (
              <button
                onClick={handleCreate}
                data-onboarding="add-ai-knowledge-button"
                className="px-3 py-1.5 text-sm font-medium bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
              >
                Add Your First Entry
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto -mx-6 px-6">
              <table className="w-full table-fixed">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50/50">
                    <th className="w-24 px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Type</th>
                    <th className="w-48 px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Content</th>
                    <th className="w-28 px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.map((item, index) => {
                    const itemType = item.type || item.metadata?.type;
                    return (
                      <tr key={item._id} className="group hover:bg-gray-50/50 transition-colors">
                        <td className="w-24 px-4 py-3">
                          <span className="inline-flex px-2 py-0.5 text-[10px] font-medium rounded-full bg-gray-100 text-gray-600">
                            {TYPE_LABELS[itemType] || itemType}
                          </span>
                        </td>
                        <td className="w-48 px-4 py-3">
                          <div className="text-sm font-medium text-gray-900 truncate" title={getItemName(item)}>
                            {getItemName(item)}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-500 truncate">
                            {item.text?.substring(0, 100) || 'No content'}
                            {item.text?.length > 100 ? '...' : ''}
                          </div>
                        </td>
                        <td className="w-28 px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-0.5 opacity-60 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => handleView(item, index)} className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors" title="View">
                              <Eye className="w-3.5 h-3.5" />
                            </button>
                            {canUpdateVectorData() && (
                              <button onClick={() => handleEdit(item)} className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors" title="Edit">
                                <Edit className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {canDeleteVectorData() && (
                              <button onClick={() => handleDelete(item)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" title="Delete">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/30 flex items-center justify-between">
                <p className="text-xs text-gray-600">
                  Showing <span className="font-medium text-gray-900">{(page - 1) * limit + 1}</span> to{' '}
                  <span className="font-medium text-gray-900">{Math.min(page * limit, total)}</span> of{' '}
                  <span className="font-medium text-gray-900">{total}</span>
                </p>
                <div className="flex gap-1">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1.5 text-sm border border-gray-200 rounded-md disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors">
                    Previous
                  </button>
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1.5 text-sm border border-gray-200 rounded-md disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors">
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </AdminCard>

      {/* Create/Edit Modal */}
      {isFormOpen && (
        <AIKnowledgeForm
          item={editingItem}
          defaultType={activeFilter === '' ? 'about' : (currentGroup.types.split(',')[0])}
          onClose={() => setIsFormOpen(false)}
          onSaved={() => { setIsFormOpen(false); fetchData(); }}
        />
      )}

      {/* View Modal */}
      {isViewOpen && viewingItem && (
        <AIKnowledgeViewModal
          item={viewingItem}
          items={data}
          currentIndex={viewingIndex}
          onNavigate={(idx) => { setViewingIndex(idx); setViewingItem(data[idx]); }}
          onClose={() => setIsViewOpen(false)}
        />
      )}
    </AdminPageLayout>
  );
}

export default AIKnowledge;
